import { useActionSheet } from '@expo/react-native-action-sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import { addDays, parseISO } from 'date-fns';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Platform, ScrollView, View } from 'react-native';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { z } from 'zod';

import { IncidentalCategory } from '@/api';
import { useCreateIncidental } from '@/api/partners/incidentals/use-create-incidental';
import { useReservation } from '@/api/partners/reservations/use-reservation';
import {
  compressAndResize,
  runWithConcurrencyLimit,
  snakeToTitleCase,
  toZonedISO,
  uniqByFilename,
  UPLOAD_MIME_TYPES,
} from '@/core';
import { Button, ControlledInput, showErrorMessage, Text } from '@/ui';
import { Calendar } from '@/ui/calendar';
import { DatetimeInput } from '@/ui/datetime-input';
import type { DropdownOption } from '@/ui/dropdown';
import Dropdown from '@/ui/dropdown';
import FileItem from '@/ui/file-item';
import { Icon } from '@/ui/icon';
import { Paperclip } from '@/ui/icons/paperclip';
import { Loader } from '@/ui/loader';

const options: any[] = [
  {
    value: IncidentalCategory.PARKING_TICKET,
    title: snakeToTitleCase(IncidentalCategory.PARKING_TICKET),
    availableDayAfterTrip: true,
    amountVisible: true,
  },
  {
    value: IncidentalCategory.PARKING_RECEIPT,
    title: snakeToTitleCase(IncidentalCategory.PARKING_RECEIPT),
    availableDayAfterTrip: true,
    amountVisible: true,
  },
  {
    value: IncidentalCategory.SPEEDING_TICKET,
    title: snakeToTitleCase(IncidentalCategory.SPEEDING_TICKET),
    availableDayAfterTrip: true,
    amountVisible: true,
  },
  {
    value: IncidentalCategory.SMOKING,
    title: snakeToTitleCase(IncidentalCategory.SMOKING),
    availableDayAfterTrip: false,
    amountVisible: true,
    amount: 150,
    description: 'Pictures required to confirm validity.',
  },
  {
    value: IncidentalCategory.PET_HAIR,
    title: snakeToTitleCase(IncidentalCategory.PET_HAIR),
    availableDayAfterTrip: false,
    amountVisible: false,
    description: 'Pictures required to confirm validity.',
  },
  {
    value: IncidentalCategory.GLASS,
    title: snakeToTitleCase(IncidentalCategory.GLASS),
    availableDayAfterTrip: false,
    amountVisible: false,
    description:
      "Attach pictures of the damage, and if approved, we'll follow up with next steps.",
  },
  {
    value: IncidentalCategory.FLAT_TIRE,
    title: snakeToTitleCase(IncidentalCategory.FLAT_TIRE),
    availableDayAfterTrip: false,
    amountVisible: true,
    amount: 100,
    description:
      'If car is found to have a flat tire upon return, please submit images - this only applies to slow leaks, patch work, minor damage and cars that are found flat after return. Major blowouts that occur during a rental will have been handled by roadside directly while on rental.',
  },
  {
    value: IncidentalCategory.SCRATCHED_RIMS,
    title: snakeToTitleCase(IncidentalCategory.SCRATCHED_RIMS),
    availableDayAfterTrip: false,
    amountVisible: true,
    amount: 100,
    description:
      'Note that not all incidents are covered, before and after pictures necessary.',
  },
  {
    value: IncidentalCategory.ACCIDENT_BODY_DAMAGE,
    title: 'Accident/Body Damage',
    availableDayAfterTrip: false,
    amountVisible: false,
    requiredImageCount: 8,
    description:
      "Attach pictures of the damage, and if approved, we'll follow up with next steps. Before pictures required to have been uploaded at rental start for approval. Please upload at least 5 photos showing the car from all angles and at least 3 of the damaged area specifically.",
  },
  {
    value: IncidentalCategory.INTERIOR_DAMAGE,
    title: snakeToTitleCase(IncidentalCategory.INTERIOR_DAMAGE),
    availableDayAfterTrip: false,
    amountVisible: false,
    requiredImageCount: 8,
    description:
      "Examples: pet hair, liquids, etc. Attach pictures of the damage, and if approved, we'll follow up with next steps. Before pictures required to have been uploaded at rental start for approval. Please upload at least 5 photos showing the car from all angles and at least 3 of the damaged area specifically.",
  },
];

export const addIncidentalFormSchema = z
  .object({
    category: z.string(),
    offenseDate: z.date(),
    amount: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const opt = options.find((o) => o.value === data.category);

    if (opt?.amountVisible) {
      if (!data.amount || data.amount.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['amount'],
          message: 'Amount is required',
        });
      }
    }
  });

export type AddIncidentalFormType = z.infer<typeof addIncidentalFormSchema>;

type AddIncidentalFormProps = {
  category: string;
  offenseDate: Date;
  amount: string;
};

export default function AddIncidental() {
  const { styles, theme } = useStyles(stylesheet);
  const local = useLocalSearchParams();
  const router = useRouter();
  const { showActionSheetWithOptions } = useActionSheet();
  const [showCalendar, setShowCalendar] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [images, setImages] = useState<ImagePicker.ImagePickerAsset[]>();

  const {
    data: reservationData,
    refetch: refetchReservation,
    isLoading: isLoadingReservation,
  } = useReservation({
    //@ts-ignore
    variables: { id: local.id },
    enabled: !!local.id,
  });

  const {
    handleSubmit,
    control,
    trigger,
    setValue,
    formState: { isValid },
    getValues,
    reset,
    watch,
  } = useForm<AddIncidentalFormType>({
    defaultValues: {
      category: options.find(
        (opt) => opt.value === IncidentalCategory.PARKING_TICKET
      )?.value,
      offenseDate: new Date(),
    },
    resolver: zodResolver(addIncidentalFormSchema),
  });

  const [category, offenseDate] = useWatch({
    control,
    name: ['category', 'offenseDate'],
  });

  useEffect(() => {
    setValue('amount', '');

    options.find((opt) => {
      if (opt.value === category && opt.amount) {
        setValue('amount', opt.amount.toString());
        trigger();
      }
    });
  }, [category]);

  const { mutateAsync: createIncidental, isPending: isCreatingIncidental } =
    useCreateIncidental();

  // Treat the calendar selection as a wall-clock time in the pickup
  // location's zone so the saved date matches how it renders in the table.
  const pickupTz = reservationData?.pickupLocation?.timezone || 'UTC';

  const formattedOptions = useMemo(() => {
    if (!reservationData?.endTime) return options;

    const now = new Date();
    const endTime = parseISO(reservationData.endTime);

    if (now > addDays(endTime, 1)) {
      return options.filter((option) => option.availableDayAfterTrip);
    }

    return options;
  }, [reservationData?.endTime]);

  const selectedOption = useMemo(() => {
    return options.find((opt) => opt.value === category);
  }, [category]);

  const handleCreateIncidental = async (data: AddIncidentalFormProps) => {
    try {
      const formattedImages =
        Platform.OS === 'web' ? images?.map((img) => img.file) : images;

      await createIncidental({
        reservationId: reservationData.id,
        category: data.category,
        offenseDate: toZonedISO(data.offenseDate, pickupTz),
        amount: data.amount ? Math.round(parseFloat(data.amount) * 100) : 0,
        images: formattedImages,
      });

      router.navigate(`/partner/reservations/${reservationData!.id}`);

      Toast.show({
        text1: 'Incidental created successfully!',
        type: 'success',
      });
    } catch (e: any) {
      showErrorMessage(e.message);
    }
  };

  const handleUploadPhotos = async (replaceImageFileName = null) => {
    let imgs = replaceImageFileName
      ? images?.filter(
          (img) => (img.fileName || img.name) !== replaceImageFileName
        )
      : images;

    if (Platform.OS === 'web') {
      try {
        const result = await DocumentPicker.getDocumentAsync({
          type: UPLOAD_MIME_TYPES,
          copyToCacheDirectory: false,
          multiple: true,
        });

        if (!result.canceled) {
          const assets = await Promise.all(
            result.assets.map(async (asset) => {
              const response = await fetch(asset.uri);
              const blob = await response.blob();

              const file = new File([blob], asset.name || 'file.jpg', {
                type: asset.mimeType || 'image/jpeg',
              });

              return {
                file,
                name: asset.name,
                uri: asset.uri,
              };
            })
          );

          setImages(uniqByFilename([...(imgs || []), ...assets]));
        }
      } catch (error) {
        console.error('Error picking file:', error);
      }

      return;
    }

    const options = [
      'Choose from Photos',
      // 'Take a Photo',
      'Upload Files',
      'Cancel',
    ];
    const selectPhotoIdx = 0;
    // const takeAPhotoIdx = 1;
    const uploadFilesIdx = 1;
    const cancelButtonIndex = 2;

    showActionSheetWithOptions(
      {
        options,
        cancelButtonIndex,
        destructiveButtonIndex: cancelButtonIndex,
      },
      async (selectedIndex: number) => {
        switch (selectedIndex) {
          // case takeAPhotoIdx: {
          //   const permission = await ImagePicker.getCameraPermissionsAsync();
          //   if (!permission.granted) {
          //     const permissionResult =
          //       await ImagePicker.requestCameraPermissionsAsync();

          //     if (!permissionResult.granted) {
          //       return;
          //     }
          //   }

          //   const result = await ImagePicker.launchCameraAsync({
          //     base64: true,
          //   });

          //   if (!result.canceled) {
          //     setImages(uniqByFilename([...imgs, ...result.assets]));
          //   }

          //   break;
          // }
          case uploadFilesIdx: {
            try {
              setUploadingPhotos(true);

              const result = await DocumentPicker.getDocumentAsync({
                type: UPLOAD_MIME_TYPES,
                copyToCacheDirectory: true,
                multiple: true,
              });

              if (!result.canceled) {
                const formattedAssets = result.assets.map((asset) => ({
                  ...asset,
                  fileName: asset.name,
                }));

                setImages(
                  uniqByFilename([...(imgs || []), ...formattedAssets])
                );
              }
            } catch (error) {
              console.error('Error picking file:', error);
            } finally {
              setUploadingPhotos(false);
            }
            break;
          }
          case selectPhotoIdx: {
            setUploadingPhotos(true);

            const result = await ImagePicker.launchImageLibraryAsync({
              mediaTypes: ImagePicker.MediaTypeOptions.Images,
              allowsEditing: false,
              allowsMultipleSelection: true,
              base64: true,
              selectionLimit: 15,
            });

            if (!result.canceled) {
              const compressedResults = await runWithConcurrencyLimit(
                result.assets.map((asset) => () => compressAndResize(asset)),
                3 // concurrency limit
              );

              const formattedResults = compressedResults.map((res, index) => ({
                ...result.assets[index],
                uri: res.uri,
                base64: res.base64,
              }));

              setImages(uniqByFilename([...(imgs || []), ...formattedResults]));
            }

            setUploadingPhotos(false);
            break;
          }

          case cancelButtonIndex:
          // Canceled
        }
      }
    );
  };

  if (isLoadingReservation || isCreatingIncidental) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ height: 250, width: 250 }} />
      </View>
    );
  }

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: theme.colors.background_1,
        paddingTop: 16,
      }}
    >
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={{
          flexGrow: 1,
          paddingBottom: 100,
        }}
        keyboardShouldPersistTaps="always"
        showsVerticalScrollIndicator={false}
      >
        <View style={{ flexGrow: 1, gap: 16 }}>
          <View style={{ zIndex: 2 }}>
            <Dropdown
              mode="single"
              label="Incidental Type"
              options={formattedOptions}
              placeHolderText="Select incidental type"
              onSelect={(selected: DropdownOption[]) => {
                setValue('category', selected[0].value);
                trigger();
              }}
            />
          </View>

          <View style={{ gap: 4, zIndex: 1 }}>
            <DatetimeInput
              label="Date & time"
              value={offenseDate}
              timeZone={pickupTz}
              onChange={(d) => {
                setValue('offenseDate', d);
                trigger();
              }}
              onCalendarPress={() => {
                setShowCalendar(true);
              }}
              containerStyle={{ width: 275, flexGrow: 0 }}
            />

            <Calendar
              mode="datetime"
              isVisible={showCalendar}
              headerTitle={'Date'}
              minDate={new Date(2000, 0, 1)}
              defaultStartDate={offenseDate}
              onHide={() => {
                setShowCalendar(false);
              }}
              onDatesSelected={(startDt, endDt) => {
                setValue('offenseDate', startDt);
                trigger();
                setShowCalendar(false);
              }}
              onDateSelected={(startDt) => {}}
              onTimeSelected={() => {}}
              range={false}
              style={{
                zIndex: 1,
                position: 'absolute',
                top: 0,
              }}
              containerStyle={{
                borderWidth: 1,
                borderColor: theme.colors.card.default.borderColor,
                shadowColor: theme.colors.card.default.borderColor,
                shadowOffset: {
                  width: 0,
                  height: 5,
                },
                shadowOpacity: 0.5,
                shadowRadius: 5,
                elevation: 10,
              }}
              id="offense-date-calendar"
              hasBackdrop={false}
              coverScreen={false}
            />
          </View>

          {options.find((opt) => opt.value === category)?.amountVisible ? (
            <ControlledInput
              control={control}
              name="amount"
              label="Amount (USD)"
              keyboardType="decimal-pad"
              containerStyle={{ maxHeight: 100 }}
              onBlur={() => {
                trigger('amount');
              }}
              onChangeText={(text) => {
                trigger('amount');
                setValue('amount', text);
              }}
              disabled={options.find((opt) => opt.value === category)?.amount}
            />
          ) : null}

          <View style={{ gap: 16 }}>
            <View style={{ gap: 16 }}>
              <Text type="bodyRegular">
                Attachments (
                {selectedOption.requiredImageCount
                  ? `at least ${selectedOption.requiredImageCount} attachments required`
                  : 'required'}
                )
              </Text>

              {options.find((opt) => opt.value === category)?.description ? (
                <View>
                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {options.find((opt) => opt.value === category)?.description}
                  </Text>
                </View>
              ) : null}
            </View>

            <Button
              text="Upload Files"
              type="secondarySmall"
              onPress={handleUploadPhotos}
              leftIcon={
                <Icon
                  type="naked"
                  icon={<Paperclip />}
                  width={12}
                  height={12}
                  fillColor={theme.colors.button.secondary.default.text}
                />
              }
              style={{ width: 175 }}
            />

            {images?.map((image, index) => {
              const imageFileName = image.fileName || image.name;
              let imageUrl = image.uri;

              return (
                <FileItem
                  key={`${imageFileName}-${index}`}
                  imageUrl={imageUrl}
                  imageFileName={imageFileName}
                  onDelete={() => {
                    setImages(images.filter((_, i) => i !== index));
                  }}
                />
              );
            })}
          </View>
        </View>
      </ScrollView>

      <View style={{ padding: 16 }}>
        <Button
          text="Submit Incidental"
          type="primary"
          onPress={handleSubmit(handleCreateIncidental)}
          disabled={
            !isValid ||
            !images?.length ||
            (selectedOption.requiredImageCount &&
              images.length < selectedOption.requiredImageCount) ||
            isCreatingIncidental
          }
        />
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  scrollView: {
    flex: 1,
    borderRadius: 16,
    paddingLeft: {
      xs: 16,
      md: 56,
    },
    paddingRight: {
      xs: 16,
      md: 56,
    },
    paddingTop: {
      xs: 16,
      md: 56,
    },
  },
  nav: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    gap: 16,
  },
}));
